import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ComboBox, Label, RecordInterface } from '@components/common/Forms';
import { Avatar } from '@components/common';
import { Dispatch, SetStateAction } from 'react';


/** Props */
interface SearchEmployerProps {
  record?: RecordInterface;
  setWorkplaceName?: Dispatch<SetStateAction<string>>;
  isMutating?: boolean;
  labelDescriptionClass?: string;
  fieldName?: string;
  className?: string;
}

/**
 * @description Search Employer autocomplete component.
 * - `record` - record function from useBjForm
 * - `setWorkplaceName` - function, set the workplace name
 * - `isMutating` - boolean, the mutation state
 * - `labelDescriptionClass` - string, additional classes for the label description
 * - `fieldName` - string, the field name for the record function
 * - `className` - string, additional classes for the component
 */
export const SearchEmployer = (props: SearchEmployerProps) => {
  // Destructure props
  const {
    record,
    setWorkplaceName,
    isMutating,
    labelDescriptionClass = '',
    fieldName = 'companyName',
    className,
  } = props;

  // Translation
  const { t } = useTranslation();

  return (
    <Label
      as="div"
      description={t('global.label.employer_name')}
      descriptionClass={labelDescriptionClass}
      {...className && { className }}
    >
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
        />
        <ComboBox
          className="pl-10"
          options={{
            valueFromKey: 'name',
            apiList: {
              fetchUrl: '/v1/employer-tooltip',
              queryParams: (query: string) => ({ q: query }),
            },
            currentFieldValue: setWorkplaceName,
          }}
          disabled={isMutating}
          // Record function, but only for new accounts,
          // when we don't have a main employer
          {...record ? record(fieldName) : {}}
          renderList={(
            item,
            index,
            listKey,
            getItemProps,
            listStyling,
            highlightedIndex,
          ) => (
            <li key={listKey} className={`${listStyling} items-center gap-2`} {...getItemProps({ item, index })}>
              <span className="shrink-0 bg-white">
                <Avatar
                  size="sm"
                  imgPath={item['logo' as keyof typeof item]}
                  name={item['name' as keyof typeof item]}
                  alt={item['name' as keyof typeof item]}
                  rounding="none"
                  extractImageName
                />
              </span>
              <span className="flex grow flex-col">
                <span className="font-semibold">
                  {item['name' as keyof typeof item]}
                </span>
                <span
                  className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}
                >
                  {item['location' as keyof typeof item]}
                </span>
              </span>
            </li>
          )}
        />
      </div>
    </Label>
  );
};
